import { FC } from 'react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { useQueryClient } from 'react-query';
import { PersonsV3 } from '@frontend/api-person';
import { useTaskCenterMutations, TaskCenterTypes } from '@frontend/api-task-center';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { PanelHeader } from '@frontend/panel-engine';
import { useTaskCenterStore, Citation } from '@frontend/task-center-scope';
import { Button, TextareaField, useAlert, Text, ButtonBar } from '@frontend/design-system';
import { TabsController, TASK_CENTER_CONSTANTS } from '../../constants';
import { useGetFormDetails, useTaskFormPatient, useAssigneeByLocation } from '../../hooks';
import { taskCenterTrackingIds } from '../../tracking-ids';
import { TaskMutationStrategy } from '../../types';
import { getTaskCenterQueryKeys } from '../../utils';
import { PersonPicker, DatePickerDialog, Switcher, LocationPicker } from '../common';
import { PatientPicker } from '../common/fields/patient-picker';
import { TaskCitation } from './citation/task-citation';
import {
  formStyle,
  headerStyle,
  inputContainerStyle,
  textFieldStyle,
  textareaFieldStyle,
  bottomBorder,
  footerStyle,
} from './create-task.styles';

const EmptyArrayResponse: UserDetails[] = [];

export const CreateTask: FC = () => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { t } = useTranslation('task-center');
  const {
    taskMode,
    setTaskMode,
    locationIds,
    locationsData,
    shouldShowLocationDropdown,
    selectedTask,
    setTab,
    setSelectedTask,
  } = useTaskCenterStore([
    'taskMode',
    'setTaskMode',
    'locationIds',
    'locationsData',
    'shouldShowLocationDropdown',
    'selectedTask',
    'setSelectedTask',
    'setTab',
  ]);
  const isEditing = taskMode === 'edit' && !!selectedTask;
  const { taskCreateMutation, taskUpdateMutation } = useTaskCenterMutations();
  const { formValues, formProps, getUpdatedValues } = useGetFormDetails(locationIds, selectedTask);
  const { setPatient, patient } = useTaskFormPatient(selectedTask?.patient?.id);

  const taskValues = getUpdatedValues();

  const disableSave = isEditing ? Object.values(taskValues).length == 0 : !formProps.isComplete;
  const titleLength = formProps.getFieldProps('title').value.length;
  const descriptionLength = formProps.getFieldProps('description').value.length;
  const trackingIdSet = taskCenterTrackingIds[isEditing ? 'edit' : 'create'];
  const source: Citation = selectedTask?.metadata ? JSON.parse(selectedTask.metadata).source : undefined;
  const selectedLocation = formProps.getFieldProps('locationId').value;
  const { data: assignees, isFetching: fetchingPatients } = useAssigneeByLocation([selectedLocation]);

  // @ts-expect-error form values are returned as string instead of task types. Will update with this ticket
  // https://linear.app/getweave/issue/BIZX-721/updated-taskcenter-create-task-form-types
  const getTaskMutation: () => TaskMutationStrategy = () => {
    const user = getUser();

    if (taskMode === 'edit' && selectedTask?.id && user?.userID) {
      return {
        type: 'update',
        mutation: taskUpdateMutation,
        params: {
          id: selectedTask.id,
          updatedBy: user.userID,
          type: selectedTask.type,
          source: selectedTask.source,
          ...taskValues,
          ...(patient && {
            patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
          }),
        },
        successText: t('Task updated successfully'),
        errorText: t('Unable to update task at this moment'),
      };
    } else if (taskMode === 'citation') {
      return {
        type: 'create',
        mutation: taskCreateMutation,
        params: {
          tasks: [
            {
              ...selectedTask,
              ...formValues,
              createdBy: user?.userID,
              ...(patient && {
                patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
              }),
              priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
            },
          ],
        },
        successText: t('Task created successfully'),
        errorText: t('Unable to create task at this moment'),
      };
    }

    return {
      type: 'create',
      mutation: taskCreateMutation,
      params: {
        tasks: [
          {
            ...taskValues,
            createdBy: user?.userID,
            ...(patient && {
              patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
            }),
            source: TaskCenterTypes.TaskSource.SOURCE_TASK_CENTER,
            priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
          },
        ],
      },
      successText: t('Task created successfully'),
      errorText: t('Unable to create task at this moment'),
    };
  };

  const saveTask = () => {
    const config = getTaskMutation();
    const onError = () => {
      alert.error(config.errorText);
    };
    const onSuccess = () => {
      alert.success(config.successText);
      queryClient.invalidateQueries({
        queryKey: getTaskCenterQueryKeys(formProps.values.status as TaskCenterTypes.TaskStatus),
      });
      if (isEditing && selectedTask) {
        queryClient.invalidateQueries({
          queryKey: getTaskCenterQueryKeys(selectedTask.status as TaskCenterTypes.TaskStatus),
        });
      }
      setTab(TabsController[formProps.values.status as keyof typeof TabsController]);
      setTaskMode(null);
      setSelectedTask(null);
      formProps.reset();
    };

    if (config.type === 'update') {
      config.mutation.mutate(config.params, { onError, onSuccess });
    } else {
      config.mutation.mutate(config.params, { onError, onSuccess });
    }
  };

  return (
    <>
      <header css={headerStyle}>
        <Button
          iconName='caret-left'
          variant='secondary'
          size='small'
          onClick={() => {
            setTaskMode(null);
            setSelectedTask(null);
            formProps.reset();
          }}
          disabled={taskCreateMutation.isLoading}
          trackingId={trackingIdSet.cancelProgressBackButton}
          label={t('Back')}
        />
        <PanelHeader.Title level={3}>{isEditing ? t('Edit Task') : t('New Task')}</PanelHeader.Title>
      </header>
      <form {...formProps.formProps} css={formStyle}>
        <TextareaField
          label=''
          css={textFieldStyle}
          {...formProps.getFieldProps('title')}
          className='create-task-title'
          containerCss={inputContainerStyle}
          error=''
          autoFocus
          maxLength={TASK_CENTER_CONSTANTS.TITLE_LIMIT}
          autoGrow={[3, 3]}
        />
        {/* If title length is > 120 then start showing the limit else don't
        This is controlled with the color property to avoid content moving up and down*/}
        <Text
          color={titleLength > TASK_CENTER_CONSTANTS.TITLE_WARNING_LIMIT ? 'subdued' : 'white'}
          textAlign='right'
          size='medium'
        >
          {`${titleLength}/${TASK_CENTER_CONSTANTS.TITLE_LIMIT}`}
        </Text>
        <TaskCitation
          source={selectedTask?.source}
          sourceId={selectedTask?.sourceId}
          citation={source}
          // Task always have a locationId with it
          locationId={selectedTask?.locationId ?? ''}
          taskId={selectedTask?.id ?? ''}
        />
        <div>
          <TextareaField
            {...formProps.getFieldProps('description')}
            label=''
            css={textareaFieldStyle}
            className='create-task-description'
            containerCss={[inputContainerStyle]}
            maxLength={TASK_CENTER_CONSTANTS.DESCRIPTION_LIMIT}
            autoGrow={[3, 6]}
          />
          <Text
            color={descriptionLength > TASK_CENTER_CONSTANTS.DESCRIPTION_WARNING_LIMIT ? 'subdued' : 'white'}
            textAlign='right'
            size='medium'
            css={bottomBorder}
          >{`${descriptionLength}/${TASK_CENTER_CONSTANTS.DESCRIPTION_LIMIT}`}</Text>
        </div>
        {locationsData.length > 1 && (
          <LocationPicker
            data={locationsData}
            label={t('Location')}
            placeholder={formProps.getFieldProps('locationId').placeholder ?? ''}
            value={formProps.values.locationId ?? ''}
            shouldRenderChildren={shouldShowLocationDropdown}
            onSelect={(value: string) => formProps.seedValues({ ...formProps.values, locationId: value })}
            trackingIds={{
              chipButton: trackingIdSet.locationDropdown,
              removeButton: trackingIdSet.removeLocationValue,
            }}
          />
        )}
        <PatientPicker
          label={t('Patient')}
          icon='user'
          placeholder={formProps.getFieldProps('patient').placeholder ?? ''}
          buttonIconName='user'
          isRemoveable={!isEditing}
          onChange={(patient) => {
            formProps.seedValues({ ...formProps.values, patient: patient?.personId ?? '' });
            setPatient(patient);
          }}
          value={patient}
          trackingIds={{
            chipButton: trackingIdSet.patientDropdown,
            removeButton: trackingIdSet.removePatientValue,
          }}
          isEditing={isEditing}
          locationId={selectedLocation}
          disabled={!selectedLocation}
        />
        <DatePickerDialog
          label={t('Due Date')}
          placeholder={formProps.getFieldProps('dueDate').placeholder ?? ''}
          onChange={(value) => formProps.seedValues({ ...formProps.values, dueDate: value })}
          value={formProps.getFieldProps('dueDate').value}
          trackingIds={{
            chipButton: trackingIdSet.dueDateDropdown,
            removeButton: trackingIdSet.removeDueDateValue,
          }}
          isRemoveable={!isEditing}
          isEditing={isEditing}
        />

        <PersonPicker
          data={assignees?.users ?? EmptyArrayResponse}
          label={t('Assignee')}
          icon='patient'
          placeholder={formProps.getFieldProps('assignee').placeholder ?? ''}
          buttonIconName='patient'
          onChange={(value) => formProps.seedValues({ ...formProps.values, assignee: value })}
          value={formProps.getFieldProps('assignee').value}
          trackingIds={{
            chipButton: trackingIdSet.assigneeDropdown,
            removeButton: trackingIdSet.removeAssigneeValue,
          }}
          isRemoveable={!isEditing}
          isEditing={isEditing}
          disabled={fetchingPatients || !assignees?.users?.length}
        />

        <Switcher
          type='status'
          icon='pending'
          label={t('Status')}
          placeholder={formProps.getFieldProps('status').placeholder ?? ''}
          buttonIconName='pending'
          onChange={(value: TaskCenterTypes.TaskStatus | '') =>
            formProps.seedValues({ ...formProps.values, status: value })
          }
          value={formProps.getFieldProps('status').value as TaskCenterTypes.TaskStatus}
          isEditing={isEditing}
          isRemoveable={false}
          trackingIds={{ chipButton: trackingIdSet.statusDropdown }}
        />
        <Switcher
          type='priority'
          icon='bar-chart'
          label={t('Priority')}
          placeholder={formProps.getFieldProps('priority').placeholder ?? ''}
          buttonIconName='no-priority'
          onChange={(value: TaskCenterTypes.PriorityType | '') =>
            formProps.seedValues({ ...formProps.values, priority: value })
          }
          value={formProps.getFieldProps('priority').value as unknown as TaskCenterTypes.PriorityType}
          trackingIds={{
            chipButton: trackingIdSet.priorityDropdown,
            removeButton: trackingIdSet.removePriorityValue,
          }}
          statusValue={formProps.values.status as TaskCenterTypes.TaskStatus}
          onClickRemove={() => formProps.seedValues({ ...formProps.values, priority: '' })}
          isEditing={isEditing}
          isRemoveable={!isEditing}
        />
      </form>
      <ButtonBar css={footerStyle}>
        <Button
          variant='secondary'
          onClick={() => {
            setTaskMode(null);
            setSelectedTask(null);
            formProps.reset();
          }}
          trackingId={trackingIdSet.cancelProgressButton}
          label={t('Cancel')}
        >
          {t('Cancel')}
        </Button>
        <Button
          loading={taskCreateMutation.isLoading}
          onClick={saveTask}
          disabled={disableSave}
          trackingId={trackingIdSet.saveProgressButton}
          label={isEditing ? t('Save') : t('Create Task')}
        >
          {isEditing ? t('Save') : t('Create Task')}
        </Button>
      </ButtonBar>
    </>
  );
};
